import { ClientEvents } from 'common/events/ClientEvents';
import { getPromoCodeDescriptionForEvent } from 'common/PromoCodeUtilities';
import { QueryParams } from 'common/urls/QueryParams';
import { omit, uniq } from 'lodash';
import QueryString from 'query-string';
import { useEffect } from 'react';
import { RouteChildrenProps } from 'react-router';
import {
  useGetAvailablePromoCodesForUserLazyQuery,
  usePromoCodeValueQueryQuery,
} from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';

import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { StringParam, useQueryParam } from 'use-query-params';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUserHook';
import { getHighestValuePromo } from './utils';

export const REVALIDATE_MEAL_PLAN_PROMO_ROUTES = [
  Routes.CONSUMER_LANDING,
  Routes.CONSUMER_MEAL_PLANS_CHECKOUT,
  Routes.CONSUMER_MEAL_PLANS,
  Routes.CONSUMER_MEAL_PLANS_PREFERENCES,
];

/*
 * Searches for any promo code available to this user. First we check the url params. If there's nothing in the url,
 * we ask the server if there's any promos available to them. If we find one:
 *   1. Apply promo code to PromoContext
 *   2. Notify applied value to user
 *   3. Remove promo code from URL query parameters (if it came from the url)
 */
export const ConsumerPromoHandler: React.FC<RouteChildrenProps> = ({ history, location }: RouteChildrenProps) => {
  const tracker = useTracker();
  const [currentUser] = useCurrentUser();
  const { setCode, unsetActiveCode, code, availablePromos } = usePromotionalsHook();
  const [, , zipCodeMetaData] = useCachedZipCode();
  const region = zipCodeMetaData?.cachedZipCodeData?.zipCode?.region?.id ?? 1;
  const zipCodeLoading = zipCodeMetaData?.cachedZipCodeDataLoading;

  const isOnMealPlanIntake = locationInRoutes(location, REVALIDATE_MEAL_PLAN_PROMO_ROUTES);
  const isOnMarketplaceCheckout = locationInRoutes(location, [Routes.CONSUMER_ORDER_FINALIZE]);

  const [availablePromosQuery] = useGetAvailablePromoCodesForUserLazyQuery();

  // 1. First check for promo code in URL query params
  const [codeFromQueryString] = useQueryParam(QueryParams.PROMO_CODE, StringParam);
  const { data: queryStringData, loading: queryStringLoading } = usePromoCodeValueQueryQuery({
    variables: { code: codeFromQueryString ?? '', isMealPlanCode: !isOnMarketplaceCheckout },
    skip: !codeFromQueryString,
    fetchPolicy: 'cache-and-network',
  });
  const promoCodeValue = queryStringData?.promoCodeValue;

  // Helper function to unset promo code from URL
  const unsetPromoCodeQueryParam = () => {
    // HACK: Disable showing that a promo code has been auto applied on start shef landing page flow
    // because we don't want the user to know that a promo code has been applied until after they
    // register on the site
    // Only show the Notification if it's from a query string (otherwise we'll likely show the red banner on explore)
    if (!location.pathname.match(/start-shef/) && codeFromQueryString) {
      const search = QueryString.parse(location.search);
      history.replace({
        pathname: location.pathname,
        search: QueryString.stringify(omit(search, [QueryParams.PROMO_CODE])),
      });
    }
  };

  // Function to fetch available promos from server
  const fetchAndApplyBestPromoCode = async () => {
    try {
      const result = await availablePromosQuery({ variables: { regionId: region } });

      if (result.error) {
        console.error(result.error);
        return;
      }

      if (result.data) {
        // Get all available promos without filtering
        const promosFromQuery = result.data.getAvailablePromoCodesForUser;
        const allPromos = uniq(availablePromos.concat(promosFromQuery));

        // Filter based region and page route
        const regionAppropriatePromos = allPromos.filter(
          (promo) => !promo.restrictedToRegionIds || promo.restrictedToRegionIds.includes(region)
        );
        const promosToCheck = regionAppropriatePromos.filter((promo) => {
          if (isOnMealPlanIntake) {
            return promo.isMealPlanEnabled;
          }
          if (isOnMarketplaceCheckout) {
            return !promo.isMealPlanExclusive;
          }
          return true;
        });

        // Then determine and apply the highest value appropriate promo
        const highestValuePromo = getHighestValuePromo(promosToCheck);
        if (highestValuePromo) {
          setCode(highestValuePromo);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 1. First useEffect: Handle promo code from URL query parameter
  useEffect(() => {
    // Skip if still loading
    if (zipCodeLoading || (codeFromQueryString && queryStringLoading)) {
      return;
    }

    // Apply promo from URL if valid
    if (codeFromQueryString && promoCodeValue && promoCodeValue.code) {
      if (promoCodeValue.error) {
        console.error(promoCodeValue.error);
      } else {
        unsetActiveCode();
        setCode(promoCodeValue.code);
      }
      unsetPromoCodeQueryParam();
    }
  }, [codeFromQueryString, queryStringLoading, zipCodeLoading]);

  // 2. Second useEffect: Fetch available promo codes if needed
  useEffect(() => {
    // Skip if URL might have a promo code (whether it's still loading or already loaded)
    // This prevents the race condition where fetchAndApplyBestPromoCode could override a URL promo code
    if (codeFromQueryString || queryStringLoading || zipCodeLoading) {
      return;
    }

    fetchAndApplyBestPromoCode();
  }, [availablePromos.length, region, currentUser?.id, zipCodeLoading, queryStringLoading]);

  // 3. Third useEffect: Handle context-specific promo code selection
  useEffect(() => {
    // Skip if still processing URL promo or loading data
    if ((codeFromQueryString && queryStringLoading) || zipCodeLoading) {
      return;
    }

    // Handle meal plan exclusive code on marketplace checkout immediately
    if (isOnMarketplaceCheckout && code && code.isMealPlanExclusive) {
      console.log('Detected meal plan exclusive code on marketplace checkout, finding alternative...');

      // First clear the meal plan exclusive code
      unsetActiveCode();

      // Find non-meal plan exclusive codes
      const nonMealPlanExclusivePromos = availablePromos.filter((promo) => !promo.isMealPlanExclusive);

      if (nonMealPlanExclusivePromos.length > 0) {
        const highestValuePromo = getHighestValuePromo(nonMealPlanExclusivePromos);
        setCode(highestValuePromo);
      }

      return;
    }

    // Handle non-meal plan enabled code on meal plan intake
    if (isOnMealPlanIntake && code && !code.isMealPlanEnabled) {
      console.log('Detected non-meal plan enabled code on meal plan intake, finding alternative...');

      // First clear the non-meal plan enabled code
      unsetActiveCode();

      // Find meal plan enabled codes
      const mealPlanEnabledPromos = availablePromos.filter((promo) => promo.isMealPlanEnabled);

      if (mealPlanEnabledPromos.length > 0) {
        const highestValuePromo = getHighestValuePromo(mealPlanEnabledPromos);
        setCode(highestValuePromo);
      }

      return;
    }

    // Handle region restricted code
    if (code && code.restrictedToRegionIds && !code.restrictedToRegionIds.includes(region)) {
      console.log('Detected region-restricted code, finding alternative...');

      // First clear the region-restricted code
      unsetActiveCode();

      // Find region-appropriate codes
      const regionAppropriatePromos = availablePromos.filter(
        (promo) => !promo.restrictedToRegionIds || promo.restrictedToRegionIds.includes(region)
      );

      if (regionAppropriatePromos.length > 0) {
        const highestValuePromo = getHighestValuePromo(regionAppropriatePromos);
        setCode(highestValuePromo);
      }
    }
  }, [
    code,
    availablePromos,
    isOnMealPlanIntake,
    isOnMarketplaceCheckout,
    region,
    zipCodeLoading,
    codeFromQueryString,
    queryStringLoading,
    setCode,
    unsetActiveCode,
    location, // Add location to dependencies to respond to route changes
  ]);

  useEffect(() => {
    if (!code) {
      return;
    }

    tracker.track(ClientEvents.AUTO_APPLIED_PROMO, {
      promoCode: code.code ?? 'ERROR_UNKNOWN',
      promoCodeType: code.promoCodeType,
      promoCodeIsPercentage: code.isPercentage,
      value: code.value,
      promoCodeDescription: getPromoCodeDescriptionForEvent(code),
    });

    // Dont want other dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return null;
};

export default ConsumerPromoHandler;
